import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import { useApiError } from '../api/describe.ts';
import type { BackupRecord, BackupSchedule, PanelInstallation } from '../api/types.ts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Confirm,
  EmptyState,
  Field,
  Notice,
  Skeleton,
  TextInput,
} from '../components/ui.tsx';
import { Technical, When, usePermission } from '../components/operational.tsx';
import '../components/operational.css';

/**
 * Backups.
 *
 * The screen has one job beyond showing a list, and it is the sentence at the
 * top: nothing is backed up on a schedule unless the customer turns it on.
 * That is a product decision — a platform that quietly starts copying a
 * customer's user database because it seemed helpful has made a choice about
 * their data that was not its to make — and the interface has to state it,
 * because an empty backups page otherwise reads as "not yet" rather than as
 * "not unless you ask".
 *
 * The other deliberate thing is how restoring is guarded. A confirmation
 * dialogue that a customer can dismiss with the same reflex that opened it is
 * not a guard, so this one requires the word to be typed, names the date being
 * restored, and says in the same breath what will be lost.
 */
export function Backups(): ReactNode {
  const { t } = useI18n();
  const [panels, setPanels] = useState<readonly PanelInstallation[] | undefined>();
  const [backups, setBackups] = useState<readonly BackupRecord[]>([]);
  const [schedules, setSchedules] = useState<readonly BackupSchedule[]>([]);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    try {
      const [installed, state] = await Promise.all([api.installedPanels(), api.backups()]);
      setPanels(installed);
      setBackups(state.backups);
      setSchedules(state.schedules);
      setError(undefined);
    } catch {
      setPanels([]);
      setError(t('error.network'));
    }
  }, [t]);

  useEffect(() => {
    // Wrapped rather than called directly: `load` sets state, and an effect
    // that does so in its own body is a cascading render. The work is
    // asynchronous anyway — every setState in it happens after an await.
    void (async () => {
      await load();
    })();
  }, [load]);

  if (!panels) {
    return (
      <Card>
        <Skeleton lines={6} />
      </Card>
    );
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1>{t('backups.title')}</h1>
          <p className="muted">{t('backups.subtitle')}</p>
        </div>
        <Button variant="secondary" onClick={() => void load()}>
          {t('common.refresh')}
        </Button>
      </header>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Notice tone="info">{t('backups.optIn')}</Notice>

      {panels.length === 0 ? (
        <Card>
          <EmptyState title={t('backups.noPanels')} />
        </Card>
      ) : (
        <div className="op-list">
          {panels.map((panel) => (
            <PanelBackups
              key={panel.id}
              panel={panel}
              schedule={schedules.find((row) => row.installationId === panel.id)}
              backups={backups.filter((row) => row.resourceId === panel.id)}
              onChanged={() => void load()}
            />
          ))}
        </div>
      )}
    </>
  );
}

const INTERVALS = [
  { hours: 6, key: 'backups.every6' },
  { hours: 12, key: 'backups.every12' },
  { hours: 24, key: 'backups.every24' },
  { hours: 168, key: 'backups.every168' },
] as const;

function PanelBackups({
  panel,
  schedule,
  backups,
  onChanged,
}: {
  panel: PanelInstallation;
  schedule: BackupSchedule | undefined;
  backups: readonly BackupRecord[];
  onChanged: () => void;
}): ReactNode {
  const { t, formatNumber, formatDateTime } = useI18n();
  const describe = useApiError();
  // Taking or scheduling a backup and putting one back are different
  // permissions, deliberately: the roles that may make a copy are not
  // automatically the roles that may replace a live panel's data with one.
  const create = usePermission('backup.create');
  const restore = usePermission('backup.restore');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ReactNode>();
  const [everyHours, setEveryHours] = useState<number>(schedule?.everyHours ?? 24);
  const [keep, setKeep] = useState<string>(String(schedule?.keep ?? 7));
  const [restoring, setRestoring] = useState<BackupRecord | undefined>();
  const [turningOff, setTurningOff] = useState(false);

  const title = panel.serverName ?? panel.adapterId;

  const run = async (action: () => Promise<ReactNode>): Promise<void> => {
    setBusy(true);
    setMessage(undefined);
    try {
      setMessage(await action());
      onChanged();
    } catch (error) {
      setMessage(<Notice tone="danger">{describe(error)}</Notice>);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={title}
        description={panel.adapterId}
        actions={
          <Button
            busy={busy}
            disabled={!create.allowed}
            title={create.reason}
            onClick={() =>
              void run(async () => {
                await api.runBackup(panel.id);
                return <Notice tone="success">{t('backups.queued')}</Notice>;
              })
            }
          >
            {t('backups.now')}
          </Button>
        }
      />

      <div className="settings-section">
        <div className="op-row__head">
          <div className="op-row__title">
            {schedule ? (
              <>
                <Badge tone="success">{t('common.on')}</Badge>
                <span>{t('backups.scheduleOn', { hours: schedule.everyHours })}</span>
              </>
            ) : (
              <>
                <Badge tone="neutral">{t('common.off')}</Badge>
                <span>{t('backups.scheduleOff')}</span>
              </>
            )}
          </div>
          {schedule ? (
            <span className="muted">
              {t('backups.nextRun', { when: formatDateTime(schedule.nextRunAt) })}
            </span>
          ) : null}
        </div>

        {schedule ? (
          <div className="op-row__actions">
            <Button
              variant="secondary"
              busy={busy}
              disabled={!create.allowed}
              title={create.reason}
              onClick={() => setTurningOff(true)}
            >
              {t('common.disable')}
            </Button>
          </div>
        ) : null}

        {turningOff ? (
          <Confirm
            title={t('common.disable')}
            confirmLabel={t('common.disable')}
            onCancel={() => setTurningOff(false)}
            onConfirm={() => {
              setTurningOff(false);
              void run(async () => {
                await api.cancelBackupSchedule(panel.id);
                return undefined;
              });
            }}
          >
            {t('backups.turnOffConfirm')}
          </Confirm>
        ) : null}

        {!schedule ? (
          <>
            <div className="form-row">
              <Field label={t('backups.every')} required>
                {(props) => (
                  <select
                    {...props}
                    className="input"
                    value={everyHours}
                    onChange={(event) => setEveryHours(Number(event.target.value))}
                  >
                    {INTERVALS.map((option) => (
                      <option key={option.hours} value={option.hours}>
                        {t(option.key)}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label={t('backups.keep')} help={t('backups.keepHelp')} required>
                {(props) => (
                  <TextInput
                    {...props}
                    type="number"
                    min={1}
                    max={365}
                    value={keep}
                    onChange={(event) => setKeep(event.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="op-row__actions">
              <Button
                busy={busy}
                disabled={!create.allowed}
                title={create.reason}
                onClick={() =>
                  void run(async () => {
                    const parsed = Number(keep);
                    await api.scheduleBackups(
                      panel.id,
                      everyHours,
                      Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : undefined,
                    );
                    return undefined;
                  })
                }
              >
                {t('backups.turnOnConfirm')}
              </Button>
            </div>
          </>
        ) : null}

        {message}

        {/* Said once, rather than on each greyed control: a role that cannot
            do any of this should learn why from one sentence. */}
        {!create.allowed && !restore.allowed ? (
          <Notice tone="neutral">{t('permission.readOnly')}</Notice>
        ) : null}

        <h3>{t('backups.history')}</h3>
        {backups.length === 0 ? (
          <p className="muted">{t('backups.empty')}</p>
        ) : (
          <table className="op-table">
            <thead>
              <tr>
                <th>{t('backups.taken')}</th>
                <th>{t('backups.size')}</th>
                <th>{t('backups.reason')}</th>
                <th>
                  <span className="visually-hidden">{t('backups.restore')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup) => (
                <BackupRow
                  key={backup.backupId}
                  backup={backup}
                  formatNumber={formatNumber}
                  canRestore={restore.allowed}
                  restoreReason={restore.reason}
                  onRestore={() => setRestoring(backup)}
                />
              ))}
            </tbody>
          </table>
        )}

        <Technical>
          <div>installation {panel.id}</div>
          <div>adapter {panel.adapterId}</div>
          {schedule ? (
            <div>
              every {schedule.everyHours}h, keep {schedule.keep ?? '—'}
            </div>
          ) : null}
        </Technical>
      </div>

      {restoring ? (
        <RestoreDialog
          backup={restoring}
          onClose={() => setRestoring(undefined)}
          onDone={() => {
            setRestoring(undefined);
            setMessage(<Notice tone="success">{t('backups.restoreStarted')}</Notice>);
            onChanged();
          }}
        />
      ) : null}
    </Card>
  );
}

function BackupRow({
  backup,
  formatNumber,
  canRestore,
  restoreReason,
  onRestore,
}: {
  backup: BackupRecord;
  formatNumber: (value: number) => string;
  canRestore: boolean;
  restoreReason: string | undefined;
  onRestore: () => void;
}): ReactNode {
  const { t } = useI18n();
  const describe = useApiError();
  const [verifying, setVerifying] = useState(false);
  const [verdict, setVerdict] = useState<ReactNode>();

  const reason =
    backup.reason === 'scheduled'
      ? t('backups.reasonScheduled')
      : backup.reason === 'restore'
        ? t('backups.reasonRestore')
        : t('backups.reasonRequested');

  return (
    <tr>
      <td data-label={t('backups.taken')}>
        <When value={backup.createdAt} />
      </td>
      <td data-label={t('backups.size')}>{formatBytes(backup.sizeBytes, formatNumber)}</td>
      <td data-label={t('backups.reason')}>
        {reason}
        {verdict ? <div className="subtle">{verdict}</div> : null}
      </td>
      <td>
        <div className="op-row__actions">
          <Button
            size="sm"
            variant="ghost"
            busy={verifying}
            title={t('backups.verifyHelp')}
            onClick={() => {
              setVerifying(true);
              setVerdict(undefined);
              void api
                .verifyBackup(backup.backupId)
                .then((result) =>
                  setVerdict(
                    result.verified
                      ? t('backups.verified')
                      : t('backups.verifyFailed', { reason: result.reason ?? '' }),
                  ),
                )
                .catch((error: unknown) => setVerdict(describe(error)))
                .finally(() => setVerifying(false));
            }}
          >
            {t('backups.verify')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={!canRestore}
            title={restoreReason}
            onClick={onRestore}
          >
            {t('backups.restore')}
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * The restore confirmation.
 *
 * Typed confirmation rather than a second button. Restoring is the one action
 * in this product that destroys data the customer put there themselves, and
 * the point of asking for the word is that it cannot be done by the same
 * gesture that opened the dialogue.
 */
function RestoreDialog({
  backup,
  onClose,
  onDone,
}: {
  backup: BackupRecord;
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const { t, formatDateTime } = useI18n();
  const describe = useApiError();
  const [typed, setTyped] = useState('');
  const field = useRef<HTMLInputElement>(null);

  /*
   * Focus moves into the confirmation when it opens.
   *
   * Without this, pressing Restore removes the button that had focus and
   * leaves a keyboard user at the top of the document, with no indication
   * that anything appeared. They are then one Tab away from a dialogue
   * asking them to destroy their data, and do not know it is there.
   */
  useEffect(() => {
    field.current?.focus();
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const word = t('backups.restoreWord');
  const confirmed = useMemo(() => typed.trim().toUpperCase() === word, [typed, word]);

  return (
    <Notice
      tone="danger"
      title={t('backups.restoreTitle')}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={!confirmed}
            onClick={() => {
              setBusy(true);
              void api
                .restoreBackup(backup.backupId)
                .then(onDone)
                .catch((raised: unknown) => setError(describe(raised)))
                .finally(() => setBusy(false));
            }}
          >
            {t('backups.restore')}
          </Button>
        </>
      }
    >
      <p>{t('backups.restoreWarning', { when: formatDateTime(backup.createdAt) })}</p>
      <Field label={t('backups.restoreType')} error={error} required>
        {(props) => (
          <TextInput
            {...props}
            ref={field}
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
          />
        )}
      </Field>
    </Notice>
  );
}

/**
 * A size somebody can read.
 *
 * Binary units, one decimal, and never "0 B" for a backup that exists: a
 * backup smaller than a kilobyte is a backup worth looking at, and rounding it
 * to nothing hides exactly that.
 */
function formatBytes(bytes: number, formatNumber: (value: number) => string): string {
  if (bytes < 1024) return `${formatNumber(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${formatNumber(Math.round(value * 10) / 10)} ${units[unit]}`;
}

export { formatBytes };
